import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    minimumHeight = "minimumHeight",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    partialOverlay = "partialOverlay",
    partialOverlayPositioning = "partialOverlayPositioning",
    disableButton = "disableButton",
    contentPadding = "contentPadding",
    centerTextVertically = "centerTextVertically",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.minimumHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.imageOverlay]: string;
        [fields.partialOverlay]: boolean;
        [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.disableButton]: boolean;
        [fields.contentPadding]: number;
        [fields.centerTextVertically]: boolean;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.customCSS]: string;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;
            text-align: center;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
};

export const Banner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const backgroundStyles =
        fields.background === "image"
            ? `background-image: url(${parseImageUrl(fields.image).url});
           background-size: cover;`
            : `background-color: ${fields.backgroundColor};`;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    let minimumHeightStyles;
    switch (fields.minimumHeight) {
        case "1/4 viewport":
            minimumHeightStyles = "min-height: 278px;";
            break;
        case "1/2 viewport":
            minimumHeightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            minimumHeightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            minimumHeightStyles = "min-height: 100vh;";
            break;
    }

    let overlayPositioningStyles;
    if (!fields.partialOverlay) {
        overlayPositioningStyles = "align-items: stretch;";
    } else if (fields.partialOverlayPositioning === "top") {
        overlayPositioningStyles = "align-items: flex-start;";
    } else if (fields.partialOverlayPositioning === "middle") {
        overlayPositioningStyles = "align-items: center;";
    } else if (fields.partialOverlayPositioning === "bottom") {
        overlayPositioningStyles = "align-items: flex-end;";
    }

    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);

    let fontSizeStyles;
    if (fields.responsiveFontSizes || fields.customFontSizes) {
        fontSizeStyles = responsiveStyleRules(fields.responsiveFontSizes, fields.customFontSizes ? fields : undefined);
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
            
            margin-top: 24px;
        `,
    };

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${minimumHeightStyles}
            ${overlayPositioningStyles}
            ${fontSizeStyles}
        `,
    };

    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
            padding: ${fields.contentPadding}px;
            ${fields.centerTextVertically &&
            `
                display: flex;
                align-items: center;
                justify-content: center;`}
        `,
    };

    // Icon configuration - this is the only change from the original!
    const getIconConfig = () => {
        if (fields.variant === "primary") {
            return {
                src: "ChevronRight",
                position: "right" as const
            };
        }
        return undefined;
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="banner-wrapper" {...wrapperStyles}>
                <StyledWrapper className="banner-overlay-wrapper" {...overlayWrapperStyles}>
                    <StyledWrapper className="banner-center-wrapper" {...styles.slideCenteringWrapperStyles}>
                        <Typography className="banner-heading">{parse(fields.heading, parserOptions)}</Typography>
                        <Typography className="banner-subheading">{parse(fields.subheading, parserOptions)}</Typography>
                        {!fields.disableButton && (
                            <Button 
                                {...styles.bannerButton} 
                                variant={fields.variant} 
                                onClick={onClick}
                                icon={getIconConfig()}
                            >
                                {fields.buttonLabel || title || url}
                            </Button>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

// Copy the widget definition from the original Banner
const basicTab = { displayName: "Basic", sortOrder: 0 };
const settingsTab = { displayName: "Settings", sortOrder: 1 };
const advancedTab = { displayName: "Advanced", sortOrder: 2 };

const defaultCustomCss = `.banner-wrapper {
}

.banner-overlay-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

const banner: WidgetModule = {
    component: Banner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.background,
                displayName: "Background Type",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Image",
                        value: "image",
                    },
                    {
                        displayName: "Color",
                        value: "color",
                    },
                ],
                defaultValue: "image",
                tab: basicTab,
                isVisible: () => true,
            },
            {
                fieldType: "Contextual",
                name: fields.image,
                displayName: "Banner Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: item => item.fields[fields.background] === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                isVisible: item => item.fields[fields.background] === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: item => item.fields[fields.background] === "color",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.imageOverlay,
                displayName: "Image Overlay Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: item => item.fields[fields.background] === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.partialOverlay,
                displayName: "Partial Image Overlay",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isVisible: item => item.fields[fields.background] === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.partialOverlayPositioning,
                displayName: "Partial Overlay Positioning",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Middle", value: "middle" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                defaultValue: "middle",
                isVisible: item => item.fields[fields.background] === "image" && item.fields[fields.partialOverlay],
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                displayName: "Banner Heading",
                editorTemplate: "RichTextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.subheading,
                displayName: "Banner Subheading",
                editorTemplate: "RichTextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.minimumHeight,
                displayName: "Banner Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "1/4 of viewport", value: "1/4 viewport" },
                    { displayName: "1/2 of viewport", value: "1/2 viewport" },
                    { displayName: "3/4 of viewport", value: "3/4 viewport" },
                    { displayName: "Full viewport", value: "fullViewport" },
                ],
                defaultValue: "1/2 viewport",
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                displayName: "Banner Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Banner Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.buttonVariant,
                displayName: "Banner Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.disableButton,
                displayName: "Disable Banner Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.centerTextVertically,
                displayName: "Center Text Vertically",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: settingsTab,
            },
            {
                fieldType: "General",
                name: fields.contentPadding,
                displayName: "Content Padding (px)",
                editorTemplate: "IntegerField",
                defaultValue: 20,
                tab: settingsTab,
                min: 0,
                max: 100,
            },
            {
                fieldType: "General",
                name: fields.responsiveFontSizes,
                displayName: "Use Responsive Font Sizes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: settingsTab,
            },
            {
                fieldType: "General",
                name: fields.customFontSizes,
                displayName: "Customize Font Sizes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isVisible: item => item.fields[fields.responsiveFontSizes],
                tab: settingsTab,
            },
            {
                fieldType: "General",
                name: fields.h1FontSize,
                displayName: "H1 Font Size (px)",
                editorTemplate: "IntegerField",
                defaultValue: 36,
                isVisible: item => item.fields[fields.responsiveFontSizes] && item.fields[fields.customFontSizes],
                tab: settingsTab,
                min: 8,
                max: 100,
            },
            {
                fieldType: "General",
                name: fields.h2FontSize,
                displayName: "H2 Font Size (px)",
                editorTemplate: "IntegerField",
                defaultValue: 30,
                isVisible: item => item.fields[fields.responsiveFontSizes] && item.fields[fields.customFontSizes],
                tab: settingsTab,
                min: 8,
                max: 100,
            },
            {
                fieldType: "General",
                name: fields.h3FontSize,
                displayName: "H3 Font Size (px)",
                editorTemplate: "IntegerField",
                defaultValue: 24,
                isVisible: item => item.fields[fields.responsiveFontSizes] && item.fields[fields.customFontSizes],
                tab: settingsTab,
                min: 8,
                max: 100,
            },
            {
                fieldType: "General",
                name: fields.h4FontSize,
                displayName: "H4 Font Size (px)",
                editorTemplate: "IntegerField",
                defaultValue: 20,
                isVisible: item => item.fields[fields.responsiveFontSizes] && item.fields[fields.customFontSizes],
                tab: settingsTab,
                min: 8,
                max: 100,
            },
            {
                fieldType: "General",
                name: fields.h5FontSize,
                displayName: "H5 Font Size (px)",
                editorTemplate: "IntegerField",
                defaultValue: 16,
                isVisible: item => item.fields[fields.responsiveFontSizes] && item.fields[fields.customFontSizes],
                tab: settingsTab,
                min: 8,
                max: 100,
            },
            {
                fieldType: "General",
                name: fields.normalFontSize,
                displayName: "Normal Text Font Size (px)",
                editorTemplate: "IntegerField",
                defaultValue: 14,
                isVisible: item => item.fields[fields.responsiveFontSizes] && item.fields[fields.customFontSizes],
                tab: settingsTab,
                min: 8,
                max: 100,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default banner;